import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { verifyPayment } from "../api/payments";
import { confirmSubscriptionOrderApi } from "../api/verification";
import { useAuth } from "../hooks/useAuth";

// Where each kind of payment continues once it is confirmed.
const NEXT_STEP = {
  VERIFICATION_FEE: {
    to: "/verifications?success=1",
    label: "See your verification requests",
  },
  PROFESSIONAL_MEMBERSHIP: { to: "/billing", label: "Go to billing" },
  INSTITUTION_SUBSCRIPTION: { to: "/earnings", label: "Go to payments" },
  ORGANISATION_SUBSCRIPTION: { to: "/earnings", label: "Go to payments" },
};

const SIGNUP_PATH = {
  ACADEMIC_INSTITUTION: "/register/institution",
  ORGANISATION: "/register/organisation",
};

/** A signed-in payment, or else a sign-up order: confirmed with the server. */
async function confirm(orderId, signedIn) {
  if (signedIn) {
    try {
      const result = await verifyPayment({
        provider: "CASHFREE",
        orderId,
        paymentId: orderId,
      });
      return { kind: "PAYMENT", type: result?.payment?.type };
    } catch (err) {
      // Not a payment of this account: it may be a sign-up order.
      if (err?.status !== 404) throw err;
    }
  }
  const order = await confirmSubscriptionOrderApi(orderId);
  return { kind: "SIGNUP", accountType: order?.accountType };
}

/**
 * Cashfree sends the payer back here (?order_id=) after a payment that left
 * the page, such as a bank's own page or a UPI app. The order is confirmed
 * with the server, which asks Cashfree; nothing is read from the browser.
 */
export function PaymentStatusPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const { localUser } = useAuth();
  const [state, setState] = useState({ status: "CHECKING" });

  useEffect(() => {
    if (!orderId) {
      setState({
        status: "FAILED",
        message: "This link has no order. Start the payment again.",
      });
      return undefined;
    }
    let current = true;
    confirm(orderId, Boolean(localUser))
      .then((result) => current && setState({ status: "PAID", ...result }))
      .catch(
        (err) =>
          current &&
          setState({
            status: "FAILED",
            message:
              err?.message ||
              "The payment couldn't be confirmed. If money left your account, it is returned by your bank within 5 to 7 days.",
          }),
      );
    return () => {
      current = false;
    };
  }, [orderId, localUser]);

  const next =
    state.kind === "SIGNUP"
      ? {
          to: SIGNUP_PATH[state.accountType] ?? "/register",
          label: "Continue the sign-up",
        }
      : (NEXT_STEP[state.type] ?? { to: "/", label: "Go to TickAndPass" });

  return (
    <div className="min-h-screen bg-[#F5F7F9] flex flex-col justify-center items-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-[#E4E8EF] p-8">
        {state.status === "CHECKING" && (
          <>
            <h1 className="text-lg font-bold text-slate-900">
              Confirming your payment
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Checking the order with Cashfree. This takes a few seconds.
            </p>
          </>
        )}
        {state.status === "PAID" && (
          <>
            <h1 className="text-lg font-bold text-slate-900">
              Payment received
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {state.kind === "SIGNUP"
                ? "Your subscription is paid. Verify your business again on the sign-up page to finish: this payment carries over, so you are not charged twice."
                : "The invoice is emailed to you."}
            </p>
            <Link
              to={next.to}
              className="mt-5 inline-block rounded-lg bg-[var(--color-primary)] px-5 py-2.5 text-sm font-semibold text-white"
            >
              {next.label}
            </Link>
          </>
        )}
        {state.status === "FAILED" && (
          <>
            <h1 className="text-lg font-bold text-slate-900">
              The payment wasn't confirmed
            </h1>
            <p className="mt-2 text-sm text-slate-600">{state.message}</p>
            <Link
              to="/"
              className="mt-5 inline-block rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700"
            >
              Go to TickAndPass
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
